import { ref } from 'vue'

// Two themes, dark first. The switch spreads the next theme out of the toggle like ink: a View Transition snapshots
// the page in the old theme, the theme changes in one go, and the new page is revealed in a growing circle. The real
// page is under the circle the whole time, so nothing blanks out or flashes back to the old colours.
const current = ref(typeof document !== 'undefined' ? (document.documentElement.getAttribute('data-theme') || 'dark') : 'dark');

// While the theme changes, CSS colour transitions are switched off (see .ck-theme-switching in sumi.css). Without that,
// the body and every link would fade from the old colours on their own clocks after the swap.
const SWITCHING = 'ck-theme-switching';

function token(name, fallback) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}

function ms(value) {
    const n = parseFloat(value);
    return /ms$/.test(value) ? n : n * 1000;
}

export function useTheme() {
    function apply(theme) {
        document.documentElement.setAttribute('data-theme', theme);
        current.value = theme;
        try { localStorage.setItem('ck-theme', theme); } catch (e) { /* private mode */ }
    }

    // Swap with no animation at all: transitions off for the swap, back on two frames later.
    function applyInstantly(theme) {
        const root = document.documentElement;
        root.classList.add(SWITCHING);
        apply(theme);
        requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove(SWITCHING)));
    }

    function toggle(originEl) {
        const next = current.value === 'light' ? 'dark' : 'light';
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!originEl || reduce || typeof document.startViewTransition !== 'function') { applyInstantly(next); return; }

        const root = document.documentElement;
        const r = originEl.getBoundingClientRect();
        const x = r.left + r.width / 2;
        const y = r.top + r.height / 2;
        // out to the farthest corner of the window, so the circle ends exactly when it covers the page
        const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));

        root.classList.add(SWITCHING);
        const transition = document.startViewTransition(() => apply(next));
        transition.ready.then(() => {
            root.animate(
                { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
                {
                    duration: ms(token('--duration-slow', '720ms')),
                    easing: token('--ease-in-out', 'cubic-bezier(0.65, 0, 0.35, 1)'),
                    pseudoElement: '::view-transition-new(root)'
                }
            );
        }).catch(() => { /* skipped (e.g. the tab was hidden): the theme is already applied */ });
        transition.finished.finally(() => root.classList.remove(SWITCHING));
    }

    return { theme: current, toggle };
}
